/**
 * Opening a design without a waterfall (ADR 0042 §"Perceived speed").
 *
 * The editor is a large chunk. Its two requests — the design and its scene — used to start only
 * after that chunk had been downloaded and run. Now the router starts them as soon as the address
 * names a design (they travel while the chunk does), and a card that the pointer rests on starts
 * them before the click.
 *
 * What is kept is the pair of running requests, for a few seconds, for at most three designs —
 * not a cache: the editor takes its entry exactly once, and anything older than its time limit is
 * fetched again, so a design is never opened from an answer that is stale in any way a slow
 * network would not also produce.
 */
import { api } from '../api/client.js';
import type { DesignDetail } from '../api/types.js';

type SceneAnswer = Awaited<ReturnType<typeof api.getScene>>;
type Loaded = [DesignDetail, SceneAnswer];

const MAX = 3;
const entries = new Map<string, { at: number; until: number; value: Promise<Loaded> }>();

const load = (designId: string): Promise<Loaded> => Promise.all([api.getDesign(designId), api.getScene(designId)]);

/** Start loading a design now; `ttlMs` is how long the answer may wait for the editor. */
export function prefetchDesign(designId: string, ttlMs: number, now = Date.now()): void {
  const seen = entries.get(designId);
  if (seen && seen.until > now && now - seen.at < HOVER_PREFETCH_MS) {
    // Asked a moment ago (a hover, then the click): the same requests, allowed to wait as long as this caller needs.
    seen.until = Math.max(seen.until, now + ttlMs);
    return;
  }
  const value = load(designId);
  // A failure is the editor's to report, when (and if) it asks: never an unhandled rejection here.
  value.catch(() => {
    if (entries.get(designId)?.value === value) entries.delete(designId);
  });
  entries.delete(designId);
  entries.set(designId, { at: now, until: now + ttlMs, value });
  while (entries.size > MAX) entries.delete(entries.keys().next().value!);
}

/** The design and its scene: the running prefetch when there is a fresh one, otherwise a new request. */
export function takeDesign(designId: string, now = Date.now()): Promise<Loaded> {
  const seen = entries.get(designId);
  entries.delete(designId);
  return seen && seen.until > now ? seen.value : load(designId);
}

/** The address no longer names this design: its prefetch (if the editor never took it) is dropped. */
export function dropDesignPrefetch(designId: string): void {
  entries.delete(designId);
}

/** How long a prefetch waits: the route's own (the chunk may take a while on a slow line) and a hover's. */
export const ROUTE_PREFETCH_MS = 15_000;
export const HOVER_PREFETCH_MS = 5_000;
